import React, { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const Calendar = () => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  
  // Get current date info
  const today = new Date();
  
  // Functions to navigate between months
  const prevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };
  
  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };
  
  // Get days in month
  const getDaysInMonth = (year, month) => {
    return new Date(year, month + 1, 0).getDate();
  };
  
  // Get day of week for the first day of the month (0 = Sunday, 1 = Monday, etc.)
  const getFirstDayOfMonth = (year, month) => {
    return new Date(year, month, 1).getDay();
  };
  
  // Format month name
  const formatMonth = (date) => {
    return date.toLocaleString('default', { month: 'long' });
  };
  
  // Generate calendar days
  const generateCalendarDays = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const daysInMonth = getDaysInMonth(year, month);
    const firstDayOfMonth = getFirstDayOfMonth(year, month);
    
    const days = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < firstDayOfMonth; i++) {
      days.push(null);
    }
    
    // Add days of the month
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(i);
    }
    
    return days;
  };
  
  // Check if a day is today
  const isToday = (day) => {
    return (
      day === today.getDate() &&
      currentMonth.getMonth() === today.getMonth() &&
      currentMonth.getFullYear() === today.getFullYear()
    );
  };
  
  // Days of the week
  const weekdays = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
  
  // Sample events (you can replace this with actual data)
  const events = {
    // Format: "YYYY-MM-DD": [{ title, type }]
    [new Date(today.getFullYear(), today.getMonth(), 10).toISOString().split('T')[0]]: [
      { title: 'Interview', type: 'interview' }
    ],
    [new Date(today.getFullYear(), today.getMonth(), 15).toISOString().split('T')[0]]: [
      { title: 'Job Posted', type: 'job' }
    ],
    [new Date(today.getFullYear(), today.getMonth(), 22).toISOString().split('T')[0]]: [
      { title: 'Application Due', type: 'deadline' }
    ]
  };
  
  // Check if a day has events
  const getEventsForDay = (day) => {
    if (!day) return [];
    
    const dateStr = new Date(
      currentMonth.getFullYear(),
      currentMonth.getMonth(),
      day
    ).toISOString().split('T')[0];
    
    return events[dateStr] || [];
  };
  
  return (
    <div className="bg-white p-6 border border-black">
      {/* Calendar header */}
      <div className="flex justify-between items-center mb-4">
        <h3 className="font-bold text-black">Calendar</h3>
        <div className="flex items-center">
          <button 
            onClick={prevMonth}
            className="w-8 h-8 border border-black rounded-full flex items-center justify-center mr-2"
          >
            <ChevronLeft size={16} />
          </button>
          <span className="text-sm font-medium text-black">
            {formatMonth(currentMonth)} {currentMonth.getFullYear()}
          </span>
          <button 
            onClick={nextMonth}
            className="w-8 h-8 border border-black rounded-full flex items-center justify-center ml-2"
          >
            <ChevronRight size={16} />
          </button>
        </div>
      </div>
      
      {/* Calendar grid */}
      <div className="mt-4">
        {/* Weekday headers */}
        <div className="grid grid-cols-7 gap-1 mb-1">
          {weekdays.map((day, index) => (
            <div key={index} className="text-center text-xs font-medium text-black">
              {day}
            </div>
          ))}
        </div>
        
        {/* Calendar days */}
        <div className="grid grid-cols-7 gap-1">
          {generateCalendarDays().map((day, index) => {
            const dayEvents = getEventsForDay(day);
            const hasEvents = dayEvents.length > 0;
            
            return (
              <div 
                key={index} 
                className={`aspect-square p-1 border ${day ? 'border-gray-200' : 'border-transparent'} ${
                  isToday(day) ? 'bg-black text-white' : 'text-black'
                }`}
              >
                {day && (
                  <>
                    <div className="text-xs font-medium">{day}</div>
                    {hasEvents && (
                      <div className="mt-1">
                        {dayEvents.map((event, eventIndex) => (
                          <div 
                            key={eventIndex} 
                            className="w-full h-1 mb-0.5 bg-black"
                            title={event.title}
                          />
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default Calendar;
